import { toast } from "@/components/ui/use-toast";
import { useLazyExportShippingRecipientsQuery } from "@/services/shipping-recipient";
import type { BlobDownload } from "@/utils/blob-response-handler";
import { downloadFromBlob } from "@/utils/download-blob";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { useCallback } from "react";
import { SECTOR_EMAILS_LABELS, SECTOR_EMAILS_TOASTS } from "../messages";
import { SectorEmailsFilters } from "../filter/types";
import { toListParams } from "./use-recipients-source";

export interface RecipientsExportActions {
  exportRecipients: () => void;
  isExporting: boolean;
}

const isDownload = (result: unknown): result is BlobDownload =>
  !!result && typeof result === "object" && "blob" in result;

export const useRecipientsExport = (
  filters: SectorEmailsFilters
): RecipientsExportActions => {
  const [trigger, state] = useLazyExportShippingRecipientsQuery();

  const exportRecipients = useCallback((): void => {
    void (async (): Promise<void> => {
      try {
        const result: unknown = await trigger(toListParams(filters)).unwrap();

        if (!isDownload(result)) {
          toast(
            SECTOR_EMAILS_TOASTS.FAILED(SECTOR_EMAILS_LABELS.EXPORT_FALLBACK)
          );
          return;
        }

        downloadFromBlob(result.blob, result.filename);
      } catch (error: unknown) {
        toast(
          SECTOR_EMAILS_TOASTS.FAILED(
            extractRtkErrorMessage(error, SECTOR_EMAILS_LABELS.EXPORT_FALLBACK)
          )
        );
      }
    })();
  }, [trigger, filters]);

  return { exportRecipients, isExporting: state.isLoading };
};
